import { createRoute, Link } from "@tanstack/react-router";
import { Route as TenantLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useTenantBudgets, type TenantBudgetSummary } from "../../hooks/useTenantBudgets";

/** Rozpočty střediska (tenantAdmin, read-only). Detail = per-karta kalkulace (princip #3). */

function statusColor(status: string): "success" | "warning" | "gray" {
  if (status === "approved") return "success";
  if (status === "draft") return "warning";
  return "gray";
}

function TenantBudgetsPage() {
  const { data, isLoading, error } = useTenantBudgets();
  const budgets = data?.budgets ?? [];

  const columns: Column<TenantBudgetSummary>[] = [
    {
      key: "month",
      header: "Měsíc",
      cell: (r) => (
        <Link to="/tenant/budgets/$budgetId" params={{ budgetId: r.budget_id }} className="font-semibold text-brand-secondary hover:underline">
          {r.month.slice(0, 7)}
        </Link>
      ),
    },
    { key: "bg", header: "Středisko", cell: (r) => r.billing_group_code },
    {
      key: "total",
      header: "Celkem",
      cell: (r) => (
        <span className="text-primary">
          {r.total_amount_kc} Kč
          {r.eur_advisory && <span className="text-tertiary"> · {r.total_amount_eur} €</span>}
        </span>
      ),
    },
    {
      key: "status",
      header: "Stav",
      cell: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
  ];

  return (
    <section aria-labelledby="tenant-budgets-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="tenant-budgets-title" className="text-2xl font-bold text-primary m-0">
          Rozpočty
        </h2>
        <p className="text-base text-tertiary m-0">
          Měsíční rozpočty střediska. Klikni na měsíc pro detailní per-karta rozpis.
        </p>
      </header>

      {error && (
        <InlineAlert variant="error">
          {error instanceof Error ? error.message : "Načtení rozpočtů selhalo."}
        </InlineAlert>
      )}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Měsíční rozpočty
            {!isLoading && <span className="ml-2 text-sm font-normal text-tertiary">({budgets.length})</span>}
          </h3>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : (
            <DataTable
              caption="Měsíční rozpočty střediska"
              columns={columns}
              rows={budgets}
              rowKey={(r) => r.budget_id}
              emptyMessage="Žádné rozpočty."
            />
          )}
        </CardBody>
      </Card>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/budgets",
  component: TenantBudgetsPage,
});
